import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  addDoc,
  doc,
  deleteDoc,
  updateDoc
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  LuTrash2,
  LuPlus,
  LuPencil,
  LuCheck,
  LuX,
  LuScissors,
  LuClock2,
} from "react-icons/lu";
import { NumericFormat } from "react-number-format";
import { app } from "../firebase";
import Loading from "../components/Loading";
import LoadingComLayout from "../components/LoadingComLayout";

const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

/* ======================= ESTILOS RESPONSIVOS ======================= */
const Main = styled.main`
  flex: 1;
  padding: 2rem 4rem;
  background: #f3f7ff;
  min-height: 100vh;
  display: flex; flex-direction: column; gap: 1.25rem;

  @media (max-width: 1280px){ padding: 2rem; }
  @media (max-width: 768px){ padding: 1rem; }
`;

const Title = styled.h2`
  font-size: 1.5rem; font-weight: 700; color: #1f1a40; margin: .25rem 0 1rem;
`;

const CardArea = styled.section`
  background: #fff; border: 1px solid #e6ecf5; border-radius: 1rem;
  box-shadow: 0 6px 20px rgba(58,121,237,.06);
`;
const CardHeader = styled.div`
  display:flex; align-items:center; gap:.6rem;
  padding: 1rem 1.1rem; border-bottom: 1px solid #eef2fb; color: #1f1a40; font-weight: 700;
  svg{ color:#3a79ed; }
`;
const CardBody = styled.div`
  padding: 1rem 1.1rem; display:flex; flex-direction:row; justify-content:flex-start; gap:.9rem; flex-wrap: wrap;
  @media (max-width: 1280px){ flex-direction:column; }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 1.25rem;
  padding: 1rem;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  min-width: 24%;

  @media (max-width: 640px) {
    padding: 0.9rem;
    border-radius: 1rem;
    min-height: 240px;
  }
`;

const CardImageWrapper = styled.label`
  width: 88px;
  height: 88px;
  margin-bottom: 1rem;
  border-radius: 50%;
  background-color: #e9e9e9;
  border: ${({ hasImage }) => (hasImage ? "none" : "2px dashed #868686")};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  transition: transform .15s ease;

  &:hover {
    transform: ${({ disabled }) => (disabled ? "none" : "translateY(-1px)")};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    font-size: 2rem;
    color: #868686;
  }

  @media (max-width: 640px) {
    width: 76px;
    height: 76px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid #ccc;
  font-size: 0.95rem;
  color: #1F1A40;
  background: #fff;

  &::placeholder { color: #9ca3af; }

  &:focus {
    outline: none;
    border-color: #3a79ed;
    box-shadow: 0 0 0 3px rgba(58, 121, 237, .15);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.6rem;
  margin-bottom: 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid #ccc;
  font-size: 0.95rem;
  color: #1F1A40;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #3a79ed;
    box-shadow: 0 0 0 3px rgba(58, 121, 237, .15);
  }
`;

const PriceInput = styled.div`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid #ccc;
  border-radius: 0.75rem;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  background: #fff;

  input {
    border: none;
    width: 100%;
    background: transparent;
    font-size: inherit;
    color: #1F1A40;
    &:focus { outline: none; }
  }
`;

const InfoText = styled.div`
  text-align: center;
  color: #1F1A40;

  strong {
    font-size: 1rem;
    display: block;
  }

  span {
    display: inline-flex;
    align-items: center;
    gap: .35rem;
    font-size: 0.9rem;
    color: #4b5563;
  }

  @media (max-width: 640px) {
    strong { font-size: 0.95rem; }
    span { font-size: 0.85rem; }
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  width: 100%;

  @media (max-width: 420px) {
    flex-direction: column;
    gap: 0.4rem;
  }
`;

const Button = styled.button`
  flex: 1;
  padding: 0.55rem;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  border-radius: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  cursor: pointer;
  background-color: ${({ danger }) => (danger ? "#ed3a3a" : "#3a79ed")};
  color: #fff;
  transition: transform .12s ease, box-shadow .12s ease, background-color .2s ease;

  &:hover {
    background-color: ${({ danger }) => (danger ? "#d53232" : "#2f66cb")};
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0,0,0,.06);
  }

  @media (max-width: 420px) {
    width: 100%;
  }
`;

const AddCard = styled(Card)`
  justify-content: center;
  color: #666;
  border: 2px dashed #bbb;
  min-height: 220px;
  cursor: pointer;
  transition: background .15s ease, transform .12s ease;

  &:hover {
    background: #f7f7f7;
    transform: translateY(-1px);
  }

  svg { margin-bottom: .25rem; }
`;

/* ======================= COMPONENTE ======================= */
export default function CadastrarServicos() {
  const [barbeariaId, setBarbeariaId] = useState(null);
  const [servicos, setServicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return navigate("/login");

      const q = query(collection(db, "barbearia"), where("criadorUid", "==", user.uid));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const id = snap.docs[0].id;
        setBarbeariaId(id);
        await carregarServicos(id);
      }
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const carregarServicos = async (id) => {
    const snap = await getDocs(collection(db, `barbearia/${id}/servico`));
    const lista = snap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      editando: false,
      file: null,
      preview: doc.data().imagem || "",
    }));
    setServicos(lista);
  };

  const handleChange = (index, campo, valor) => {
    const novos = [...servicos];
    novos[index][campo] = valor;
    setServicos(novos);
  };

  const handleImage = (index, file) => {
    const novos = [...servicos];
    novos[index].file = file;
    novos[index].preview = URL.createObjectURL(file);
    setServicos(novos);
  };

  const uploadImagem = async (file, nome) => {
    if (!barbeariaId) throw new Error("BarbeariaId não definido");
    const imageRef = ref(storage, `${barbeariaId}/servicos/${nome}-${Date.now()}.jpeg`);
    await uploadBytes(imageRef, file);
    return await getDownloadURL(imageRef);
  };

  const confirmar = async (index) => {
    const s = servicos[index];
    if (!s.nome || !s.valor) return alert("Preencha corretamente.");

    let url = s.imagem;
    if (s.file) url = await uploadImagem(s.file, s.nome);

    const servicoData = {
      nome: s.nome,
      valor: parseFloat(s.valor),
      tempo: parseInt(s.tempo),
      imagem: url,
    };

    if (s.id) {
      await updateDoc(doc(db, `barbearia/${barbeariaId}/servico/${s.id}`), servicoData);
    } else {
      await addDoc(collection(db, `barbearia/${barbeariaId}/servico`), servicoData);
    }

    await carregarServicos(barbeariaId);
  };

  const editar = (index) => {
    const novos = [...servicos];
    novos[index].editando = true;
    setServicos(novos);
  };

  const cancelar = (index) => {
    const novos = [...servicos];
    if (!novos[index].id) {
      novos.splice(index, 1);
    } else {
      novos[index].editando = false;
    }
    setServicos(novos);
  };

  const excluir = async (index) => {
    const servico = servicos[index];

    const confirmarExcluir = window.confirm("Tem certeza que deseja excluir este serviço?");
    if (!confirmarExcluir) return;

    if (servico.id) {
      await deleteDoc(doc(db, `barbearia/${barbeariaId}/servico/${servico.id}`));
      await carregarServicos(barbeariaId);
    } else {
      const novos = [...servicos];
      novos.splice(index, 1);
      setServicos(novos);
    }
  };

  const adicionar = () => {
    setServicos([
      ...servicos,
      {
        nome: "",
        valor: "50",
        tempo: "30",
        imagem: "",
        preview: "",
        file: null,
        editando: true,
      },
    ]);
  };

  return (
    <>
      <Main>

        <CardArea>
          <CardHeader><LuScissors/>Gerencie Serviços</CardHeader>
          <CardBody>
            {servicos.map((s, index) => (
              <Card key={index}>
                <input
                  type="file"
                  id={`file-${index}`}
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleImage(index, e.target.files[0])}
                />
                <CardImageWrapper
                  htmlFor={s.editando ? `file-${index}` : undefined}
                  hasImage={!!s.preview}
                  disabled={!s.editando}
                >
                  {s.preview ? <img src={s.preview} alt="preview" /> : <LuScissors />}
                </CardImageWrapper>
                {s.editando ? (
                  <>
                    <Input
                      placeholder="Nome"
                      value={s.nome}
                      onChange={(e) => handleChange(index, "nome", e.target.value)}
                    />
                    <PriceInput>
                      <NumericFormat
                        value={s.valor}
                        onValueChange={(values) =>
                          handleChange(index, "valor", values.floatValue || "")
                        }
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="R$ "
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </PriceInput>
                    <Select
                      value={s.tempo}
                      onChange={(e) => handleChange(index, "tempo", e.target.value)}
                    >
                      {[15, 30, 45, 60, 75, 90].map((min) => (
                        <option key={min} value={min}>
                          {min} min
                        </option>
                      ))}
                    </Select>
                    <ActionRow>
                      <Button onClick={() => confirmar(index)}>
                        <LuCheck /> Confirmar
                      </Button>
                      <Button onClick={() => cancelar(index)} danger>
                        <LuX /> Cancelar
                      </Button>
                    </ActionRow>
                  </>
                ) : (
                  <>
                    <InfoText>
                      <strong>{s.nome}</strong>
                      <span>
                        R$ {s.valor?.toFixed ? s.valor.toFixed(2) : s.valor} • <LuClock2 /> {s.tempo}min
                      </span>
                    </InfoText>
                    <ActionRow>
                      <Button onClick={() => editar(index)}>
                        <LuPencil /> Editar
                      </Button>
                      <Button onClick={() => excluir(index)} danger>
                        <LuTrash2 /> Excluir
                      </Button>
                    </ActionRow>
                  </>
                )}
              </Card>
            ))}
            <AddCard onClick={adicionar}>
              <LuPlus size={24} />
              Adicionar Serviço
            </AddCard>
          </CardBody>
        </CardArea>
      </Main>
    </>
  );
}
